import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, CircularProgress, Alert,
  FormControlLabel, Switch, Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  TaskAlt as ReviewIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import ErpClientFormFields, {
  emptyClientForm,
  toClientForm,
  applyFieldChange,
  toClientPayload,
  clientFormatErrors,
} from './ErpClientFormFields';
import ErpCostCentersTab from './ErpCostCentersTab';
import { InfoTip } from '../LabelWithInfo';
import ErpAgreementTab from './ErpAgreementTab';
import ErpClientTalanaLink from './ErpClientTalanaLink';
import formatRut from '../../erp/formatRut';
import { formatDate } from '../formatDate';

const toDateLabel = (value) => (value ? formatDate(value) : ''); // DD/MM/AAAA

/**
 * Detalle del cliente ERP: Datos | Centros de costo | Acuerdo comercial.
 */
const ErpClientDetailLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clientId } = useParams();
  const { organization, authToken, showAlert } = useStore.getState();

  const [client, setClient] = useState(null);
  const [form, setForm] = useState(emptyClientForm);
  const [statusActive, setStatusActive] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadClient = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/erp/clients/${clientId}`, {
        params: { organizationId: organization },
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (response.data.response?.status_response === 'OK') {
        const data = response.data.response.data;
        setClient(data);
        setForm(toClientForm(data));
        setStatusActive(data.status === 'active');
      } else {
        setError(t('erp.clients.loadError'));
      }
    } catch (err) {
      console.error(err);
      setError(t('erp.clients.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organization && clientId) loadClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, clientId]);

  const handleFieldChange = (path, value) =>
    setForm((prev) => applyFieldChange(prev, path, value));

  const handleSave = async () => {
    if (!form.legalName.trim() || !form.rut.trim()) {
      showAlert(t('erp.common.requiredFields'), 'warning');
      return;
    }
    if (Object.keys(clientFormatErrors(form)).length > 0) {
      showAlert(t('erp.validation.fixErrors'), 'warning');
      return;
    }

    try {
      setSaving(true);

      const response = await api.put(
        `/erp/clients/${clientId}`,
        {
          ...toClientPayload(form),
          status: statusActive ? 'active' : 'inactive',
          organizationId: organization,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.response?.status_response === 'OK') {
        showAlert(t('erp.clients.saveSuccess'), 'success');
        setClient(response.data.response.data);
      } else {
        showAlert(t('erp.clients.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.errorMessage || '';
      if (message.includes('Invalid RUT')) {
        showAlert(t('erp.clients.invalidRut'), 'error');
      } else if (message.includes('already exists')) {
        showAlert(t('erp.clients.duplicateRut'), 'error');
      } else {
        showAlert(t('erp.clients.saveError'), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReview = async () => {
    try {
      const response = await api.post(
        `/erp/clients/${clientId}/review`,
        { organizationId: organization },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.response?.status_response === 'OK') {
        setClient(response.data.response.data);
        showAlert(t('erp.clients.review.done'), 'success');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('erp.clients.saveError'), 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !client) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || t('erp.clients.loadError')}</Alert>
      </Box>
    );
  }

  const reviewed = Boolean(client.review?.reviewedAt);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/erp/clients')} sx={{ textTransform: 'none' }}>
          {t('erp.clients.title')}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>{client.legalName}</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatRut(client.rut)} · {t('erp.clients.form.auxiliaryRut')}: {client.auxiliaryRut || '—'}
            <InfoTip title={t('erp.tooltips.client.auxiliaryRut')} />
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErpClientTalanaLink client={client} onChanged={loadClient} />
          {reviewed ? (
            <Chip
              icon={<ReviewIcon />}
              color="success"
              variant="outlined"
              label={t('erp.clients.review.doneAt', { date: toDateLabel(client.review?.reviewedAt) })}
            />
          ) : (
            <Tooltip title={t('erp.tooltips.client.reviewBp')}>
              <Button
                variant="outlined"
                startIcon={<ReviewIcon />}
                onClick={handleReview}
                sx={{ textTransform: 'none' }}
              >
                {t('erp.clients.review.button')}
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
        <Tabs
          value={currentTab}
          onChange={(e, v) => setCurrentTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label={t('erp.clients.detailTabs.data')} sx={{ textTransform: 'none' }} />
          <Tab label={t('erp.clients.detailTabs.costCenters')} sx={{ textTransform: 'none' }} />
          <Tab label={t('erp.clients.detailTabs.agreement')} sx={{ textTransform: 'none' }} />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {currentTab === 0 && (
            <>
              <ErpClientFormFields form={form} onChange={handleFieldChange} isEdit />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={statusActive}
                      onChange={(e) => setStatusActive(e.target.checked)}
                    />
                  )}
                  label={statusActive ? t('erp.clients.status.active') : t('erp.clients.status.inactive')}
                />
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  disabled={saving || Object.keys(clientFormatErrors(form)).length > 0}
                  onClick={handleSave}
                  sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}
                >
                  {saving ? t('erp.common.saving') : t('erp.common.save')}
                </Button>
              </Box>
            </>
          )}

          {currentTab === 1 && <ErpCostCentersTab clientId={clientId} />}

          {currentTab === 2 && <ErpAgreementTab clientId={clientId} />}
        </Box>
      </Paper>
    </Box>
  );
};

export default ErpClientDetailLayout;
